import { useCallback, useRef } from "react";
import type { ResourceResponse } from "types";
import { useAppDispatch } from "hooks/store";
import { ResponseCode } from "consts/api";
import { isResponseCode, ManagementApiResponseAdapter } from "util/api";
import useGeneralActions from "features/general/useActions";
import { logout } from "features/auth/slice";
import { isResponseValid } from "features/auth/helpers";
import type { RequestSettings } from "./types";
import { getNotificationSettings, withDefaults } from "./helpers";

export const useSimpleRequest = (settings: RequestSettings = {}) => {
    const dispatch = useAppDispatch();
    const { showNotication } = useGeneralActions();

    const helpers = {
        settings,
        showNotication,
        dispatch
    };
    const helpersRef = useRef(helpers);
    helpersRef.current = helpers;

    return useCallback(async <T = any>(
        apiHandler: () => Promise<Response>,
        requestSettings: RequestSettings<T> = {}
    ) => {
        const {
            settings,
            showNotication,
            dispatch
        } = helpersRef.current;

        const {
            processResponseStrategy,
            handleShouldNotifyOnSuccess,
            setLoading,
            onSuccess,
            onError
        } = withDefaults(settings, requestSettings);

        try {
            setLoading(true);

            const response = await apiHandler();

            const payload: ResourceResponse<T> = ManagementApiResponseAdapter.from<T>(
                await response[processResponseStrategy](),
                response
            );

            if (!payload.success) {
                if (!isResponseValid(payload)) {
                    dispatch(logout());
                }

                showNotication(
                    getNotificationSettings({
                        ...payload,
                        code: ResponseCode.BadRequest
                    })
                );

                if (isResponseCode(
                    payload,
                    ResponseCode.UnprocessibleEntity
                )) {
                    showNotication(
                        getNotificationSettings(payload)
                    );
                }

                onError(payload);

                return payload;
            }

            if (handleShouldNotifyOnSuccess(payload)) {
                showNotication(
                    getNotificationSettings(payload)
                );
            }

            onSuccess(payload);

            return payload;
        } finally {
            setLoading(false);
        }
    }, []);
};

export default useSimpleRequest;
